import { ART_PALETTE } from '../art-palette.js';
import { MASK, PIPE_MASKS } from './masks.js';
import { blankPixels, plainTile } from './helpers.js';
import { windowPixels } from './buildings.js';

/**
 * Interior kit — flat hybrid house style (docs/art-direction-rules.md §0).
 *
 * Every discrete prop is a flat field with three values (lit cap / body / dark
 * underside), a full Ink contour including diagonal corner gaps, and a contact
 * pad so it sits on the floor instead of floating. Materials share four ramps
 * across the whole set, so a chest, a wardrobe and a bed read as the same wood.
 */
const INK = 1;
/** Interior contact / cast shadow. Deep shadow reads on wood and stone floors
 *  where §0.5's Dark grass ground pad would not. */
const SHADE = 2;

/** Shared material ramps. Three steps each; wood adds one recess step. */
const WOOD = { lit: 9, body: 8, mid: 7, dark: 3 };
const METAL = { lit: 27, body: 26, mid: 25, dark: 24 };
const CLOTH = { lit: 26, body: 29, dark: 24 };
const PAPER = { lit: 23, body: 11, dark: 9 };

const OUTLINE_DIRS = [[0, -1], [1, 0], [0, 1], [-1, 0], [1, 1], [1, -1], [-1, 1], [-1, -1]];

/**
 * Build one 16×16 matrix with the house-style drawing verbs.
 * Draw the form, call `outline()` once, then `contact()` or `cast()`.
 */
function sprite(draw) {
  const px = blankPixels();
  const put = (x, y, color) => {
    if (x >= 0 && x < 16 && y >= 0 && y < 16 && color != null) px[y][x] = color;
  };
  const rect = (x, y, width, height, color) => {
    for (let j = y; j < y + height; j++) for (let i = x; i < x + width; i++) put(i, j, color);
  };
  const hline = (x, y, width, color) => rect(x, y, width, 1, color);
  const vline = (x, y, height, color) => rect(x, y, 1, height, color);

  /** Full contour around every opaque cluster, diagonal gaps included (§0.1). */
  const outline = (color = INK) => {
    const copy = px.map((row) => [...row]);
    for (let y = 0; y < 16; y++) for (let x = 0; x < 16; x++) {
      if (copy[y][x]) continue;
      if (OUTLINE_DIRS.some(([dx, dy]) => copy[y + dy]?.[x + dx])) px[y][x] = color;
    }
  };

  /** Ground pad under a free-standing prop (§0.5), on the floor row. */
  const contact = (x0, x1, y = 15) => {
    for (let x = x0; x <= x1; x++) if (px[y]?.[x] === 0) put(x, y, SHADE);
  };

  /** Drop shadow thrown on the wall plane behind a hung object. */
  const cast = (dx = 1, dy = 1) => {
    const copy = px.map((row) => [...row]);
    for (let y = 0; y < 16; y++) for (let x = 0; x < 16; x++) {
      if (!copy[y][x]) continue;
      if (copy[y + dy]?.[x + dx] === 0) put(x + dx, y + dy, SHADE);
    }
  };

  draw({ px, put, rect, hline, vline, outline, contact, cast });
  return px;
}

// ---------------------------------------------------------------------------
// Interior room-face walls (pipe4)
// ---------------------------------------------------------------------------

/**
 * Wall materials. `fill` is the flat field, `light` the lit cap under the top
 * contour, `shadow` the shade line below that cap (and the panel/dado grooves),
 * `trim` the baseboard face and the two sanctioned surface marks.
 */
const WALL_SCHEMES = {
  wood: { fill: 8, light: 9, shadow: 3, trim: 7 },
  stone: { fill: 25, light: 27, shadow: 24, trim: 26 },
  cream: { fill: 11, light: 23, shadow: 7, trim: 10 },
  blue: { fill: 19, light: 21, shadow: 18, trim: 20 },
  green: { fill: 14, light: 16, shadow: 12, trim: 13 },
  red: { fill: 5, light: 6, shadow: 3, trim: 4 },
  panel: { fill: 7, light: 9, shadow: 3, trim: 8 },
  wainscot: { fill: 10, light: 23, shadow: 7, trim: 11 },
};

/**
 * The plane sits inside the shell it is painted on, so it keeps clear of the
 * three-pixel structure trim the shell draws on its outward faces.
 */
const WALL_INSET = 3;

/**
 * Vertical cross-section of the wall face, one entry per row.
 *
 * A wall cell paints `wall` at its own cell and `roof` one cell south, so the
 * halves always meet at top row 15 / bottom row 0 — both stay plain field. The
 * lit lip sits under the top contour and the baseboard at the foot, so a wall
 * run reads as one vertical plane: contour, lit cap, shade line, flat field,
 * lit skirting, baseboard, floor line.
 *
 * The array is also the canonical E/W column, so every legal horizontal join is
 * pixel-exact by construction and no contour lands on a same-material seam.
 */
function wallProfile(kind, half) {
  const { fill, light, shadow, trim } = WALL_SCHEMES[kind];
  const column = Array(16).fill(fill);
  if (half === 'top') {
    for (let y = 0; y < WALL_INSET; y++) column[y] = 0;
    column[WALL_INSET] = INK;
    column[WALL_INSET + 1] = light;
    column[WALL_INSET + 2] = light;
    column[WALL_INSET + 3] = shadow;
    if (kind === 'panel') {
      // A chair rail, not vertical stiles: horizontal bands stay continuous
      // across every E/W join, where a stile per cell would print the grid.
      column[10] = light;
      column[11] = trim;
    }
  } else {
    if (kind === 'wainscot') {
      column[5] = light;
      for (let y = 6; y < 15; y++) column[y] = trim;
    }
    column[12] = light;    // skirting catches the light on its top edge
    column[13] = trim;
    column[14] = shadow;   // darker base band, so the plane reads as vertical
    column[15] = INK;      // floor line
  }
  return column;
}

/**
 * NOTE ON SURFACE TEXTURE (§0.4). Walls carry no per-cell marks at all. Any
 * grain speck, joint or stile repeats once per 16px across a run and prints the
 * tile grid at 1×, which §6 rejects as an obvious repeating super-pattern. The
 * materials separate on hue and value instead, and `panel` / `wainscot` get a
 * horizontal rail and dado from the row profile — horizontal bands are the only
 * relief that stays continuous across every E/W join.
 */

/**
 * Interior room-face walls (pipe4).
 * `half='top'` → upper plane with the lit cap (components.wall).
 * `half='bottom'` → lower plane and baseboard (components.roof, one cell south).
 */
function interiorWallVariants(kind, half = 'top') {
  const profile = wallProfile(kind, half);
  const { fill } = WALL_SCHEMES[kind];

  return Object.fromEntries(PIPE_MASKS.map((mask) => {
    const p = blankPixels();
    for (let y = 0; y < 16; y++) for (let x = 0; x < 16; x++) p[y][x] = profile[y];

    // No N/S rewrite. A wall cell paints its lower plane over the cell to the
    // south, and the wall standing there paints its upper plane on top of it,
    // so the lower plane's baseboard rows are already hidden whenever S is set.
    // Leaving the profile intact keeps every E/W join pixel-exact for free.


    // Exposed ends close the plane with an Ink contour; connected ends keep the
    // canonical cross-section so horizontal runs join without a visible seam.
    for (let y = 0; y < 16; y++) {
      if (!(mask & MASK.W)) {
        for (let x = 0; x < WALL_INSET; x++) p[y][x] = 0;
        if (p[y][WALL_INSET]) p[y][WALL_INSET] = INK;
      }
      if (!(mask & MASK.E)) {
        for (let x = 0; x < WALL_INSET; x++) p[y][15 - x] = 0;
        if (p[y][15 - WALL_INSET]) p[y][15 - WALL_INSET] = INK;
      }
    }
    return [mask, p];
  }));
}

// ---------------------------------------------------------------------------
// Wall objects — hung on the wall plane: contour plus a drop shadow below
// ---------------------------------------------------------------------------

/**
 * Hung objects live in rows 4–14: the wall plane starts at row 3 (the three
 * rows above it belong to the shell), and row 15 carries the drop shadow. So
 * every wall object's Ink contour lands on wall, never on the shell trim.
 */
function clockPixels() {
  return sprite(({ put, vline, hline, outline, cast }) => {
    for (let y = 3; y < 15; y++) for (let x = 2; x < 14; x++) {
      const d = (x - 7.5) ** 2 + (y - 8.5) ** 2;
      if (d > 30) continue;
      if (d <= 13) { put(x, y, PAPER.body); continue; }
      put(x, y, x <= 7 && y <= 8 ? WOOD.lit : x >= 8 && y >= 9 ? WOOD.dark : WOOD.body);
    }
    hline(5, 5, 5, PAPER.lit);
    vline(7, 6, 4, INK);
    hline(8, 9, 3, INK);
    outline();
    cast();
  });
}

/** Wood frame plus a recessed canvas; `paint` fills the 8×6 canvas at (4,6). */
function framedPicture(paint) {
  return sprite((g) => {
    const { rect, hline, vline, outline, cast } = g;
    rect(2, 4, 12, 10, WOOD.body);
    hline(2, 4, 12, WOOD.lit);
    hline(2, 13, 12, WOOD.dark);
    vline(13, 4, 10, WOOD.dark);
    rect(4, 6, 8, 6, WOOD.mid);
    paint(g);
    outline();
    cast();
  });
}

function wallDecorationPixels(kind) {
  if (kind === 'painting-landscape') {
    return framedPicture(({ rect, hline, put }) => {
      rect(4, 6, 8, 3, 21);
      rect(4, 9, 8, 3, 14);
      hline(4, 9, 8, 13);
      put(10, 7, PAPER.body);
      rect(6, 10, 2, 2, 12);
    });
  }
  if (kind === 'painting-portrait') {
    return framedPicture(({ rect, hline }) => {
      rect(4, 6, 8, 6, CLOTH.body);
      hline(4, 6, 8, CLOTH.lit);
      rect(6, 7, 4, 2, WOOD.dark);
      rect(6, 9, 4, 1, PAPER.body);
      rect(5, 10, 6, 2, 28);
    });
  }
  if (kind === 'painting-still-life') {
    return framedPicture(({ rect, hline, put }) => {
      rect(4, 6, 8, 6, PAPER.body);
      hline(4, 10, 8, WOOD.mid);
      rect(6, 8, 4, 2, METAL.body);
      rect(6, 6, 3, 2, 28);
      put(9, 7, 12);
    });
  }
  if (kind === 'painting-seascape') {
    return framedPicture(({ rect, hline, put }) => {
      rect(4, 6, 8, 3, 21);
      rect(4, 9, 8, 3, 19);
      hline(4, 9, 8, 20);
      for (const [x, y] of [[5, 10], [8, 11], [10, 10]]) put(x, y, 22);
    });
  }
  if (kind === 'mirror') {
    return sprite(({ rect, hline, vline, put, outline, cast }) => {
      rect(4, 4, 8, 10, METAL.body);
      hline(4, 4, 8, METAL.lit);
      vline(11, 4, 10, METAL.dark);
      hline(4, 13, 8, METAL.dark);
      rect(5, 5, 6, 8, 21);
      rect(5, 11, 6, 2, 20);
      for (const [x, y] of [[6, 7], [7, 6], [8, 10], [7, 11]]) put(x, y, 22);
      outline();
      cast();
    });
  }
  if (kind === 'shelf') {
    return sprite(({ rect, hline, vline, put, outline, cast }) => {
      // Objects first, then the plank they stand on.
      rect(3, 5, 2, 4, 28);
      vline(5, 4, 5, CLOTH.body);
      rect(6, 6, 2, 3, PAPER.body);
      rect(9, 6, 3, 3, METAL.body);
      hline(9, 6, 3, METAL.lit);
      rect(2, 9, 12, 2, WOOD.body);
      hline(2, 9, 12, WOOD.lit);
      hline(2, 10, 12, WOOD.dark);
      vline(4, 11, 2, WOOD.dark);
      vline(11, 11, 2, WOOD.dark);
      put(8, 8, WOOD.dark);
      outline();
      cast();
    });
  }
  if (kind === 'sconce') {
    return sprite(({ rect, hline, vline, outline, cast }) => {
      hline(5, 4, 6, PAPER.lit);            // shade cap
      rect(4, 5, 8, 2, PAPER.body);
      hline(4, 7, 8, PAPER.dark);
      hline(5, 8, 6, WOOD.lit);             // warm spill under the shade
      vline(7, 9, 3, METAL.mid);            // arm
      vline(8, 9, 3, METAL.body);
      rect(6, 12, 4, 2, METAL.body);        // backplate
      hline(6, 12, 4, METAL.lit);
      hline(6, 13, 4, METAL.dark);
      outline();
      cast();
    });
  }
  if (kind === 'tapestry') {
    return sprite(({ rect, hline, vline, put, outline, cast }) => {
      hline(2, 4, 12, WOOD.mid);            // rod
      put(1, 4, WOOD.dark);
      put(14, 4, WOOD.dark);
      rect(3, 5, 10, 9, CLOTH.body);
      hline(3, 5, 10, CLOTH.lit);
      vline(12, 5, 9, CLOTH.dark);
      hline(3, 13, 10, CLOTH.dark);
      rect(6, 7, 4, 4, 30);
      rect(7, 8, 2, 2, PAPER.body);
      for (const x of [4, 6, 8, 10]) put(x, 14, CLOTH.dark);
      outline();
      cast();
    });
  }
  throw new Error(`Unknown wall decoration: ${kind}`);
}

// ---------------------------------------------------------------------------
// Ground furniture
//
// Size hierarchy (§3 — footprint before detail). A prop's mass is sized to what
// it is, not to the cell it happens to sit in, so a room reads as furnished:
//   large  x1–14  bed, table, sofa, bathtub, wardrobe, bookshelf, stove,
//                 dresser, desk, cabinets, rug — these fill their cell.
//   medium x2–13  armchair, chests, vanity, toilet, coat rack, plant.
//   small  x4–11  nightstand, standing lamp, laundry basket.
// Row 15 is always reserved for the contact pad.
// ---------------------------------------------------------------------------

function furniturePixels(kind) {
  if (kind === 'bed') {
    return sprite(({ rect, hline, vline, outline, contact }) => {
      rect(1, 1, 14, 13, WOOD.mid);
      hline(1, 1, 14, WOOD.lit);            // headboard cap
      rect(2, 2, 12, 2, WOOD.body);         // headboard face
      rect(2, 4, 12, 3, PAPER.body);        // pillow
      hline(2, 6, 12, PAPER.dark);
      rect(2, 7, 12, 5, CLOTH.body);        // blanket
      hline(2, 7, 12, CLOTH.lit);           // turned-down hem
      vline(13, 7, 5, CLOTH.dark);
      hline(2, 11, 12, CLOTH.dark);
      hline(1, 12, 14, WOOD.body);          // foot rail
      hline(1, 13, 14, WOOD.dark);
      outline();
      contact(2, 15);
    });
  }
  return sprite(({ rect, hline, vline, outline, contact }) => {
    rect(1, 3, 14, 7, WOOD.body);           // table top
    hline(1, 3, 14, WOOD.lit);
    hline(1, 9, 14, WOOD.dark);
    vline(14, 3, 7, WOOD.dark);
    hline(2, 10, 12, WOOD.mid);             // apron
    for (const x of [2, 12]) {
      rect(x, 11, 2, 3, WOOD.mid);
      vline(x + 1, 11, 3, WOOD.dark);
    }
    outline();
    contact(1, 15);
  });
}

// ---------------------------------------------------------------------------
// Interior props
// ---------------------------------------------------------------------------

/** Book spines: flat fields on the shared ramps, one warm accent. */
const BOOK_COLORS = [28, CLOTH.body, PAPER.body, WOOD.lit];

function interiorPropPixels(kind) {
  if (kind === 'cabinet') {
    return sprite(({ rect, hline, vline, put, outline, contact }) => {
      rect(1, 2, 14, 3, METAL.body);        // stone counter
      hline(1, 2, 14, METAL.lit);
      hline(1, 4, 14, METAL.dark);
      rect(2, 5, 12, 9, WOOD.body);
      hline(2, 5, 12, WOOD.mid);            // under-counter occlusion
      vline(13, 5, 9, WOOD.dark);
      hline(2, 13, 12, WOOD.dark);
      vline(7, 6, 7, WOOD.dark);
      vline(8, 6, 7, WOOD.mid);
      put(6, 9, METAL.lit); put(9, 9, METAL.lit);
      outline();
      contact(1, 15);
    });
  }

  if (kind === 'wall-cabinet') {
    return sprite(({ rect, hline, vline, put, outline, cast }) => {
      rect(2, 1, 12, 11, WOOD.body);
      hline(2, 1, 12, WOOD.lit);
      vline(13, 1, 11, WOOD.dark);
      hline(2, 11, 12, WOOD.dark);
      vline(7, 2, 9, WOOD.dark);
      vline(8, 2, 9, WOOD.mid);
      put(6, 6, METAL.lit); put(9, 6, METAL.lit);
      outline();
      cast(1, 2);
    });
  }

  if (kind === 'sink-cabinet') {
    return sprite(({ rect, hline, vline, put, outline, contact }) => {
      rect(7, 0, 2, 3, METAL.mid);          // faucet
      hline(7, 0, 3, METAL.body);
      put(9, 1, METAL.lit);
      rect(1, 3, 14, 3, METAL.body);        // basin deck
      hline(1, 3, 14, METAL.lit);
      hline(1, 5, 14, METAL.dark);
      rect(5, 3, 6, 2, METAL.mid);
      hline(6, 4, 4, 20);
      rect(2, 6, 12, 8, WOOD.body);
      hline(2, 6, 12, WOOD.mid);
      vline(13, 6, 8, WOOD.dark);
      hline(2, 13, 12, WOOD.dark);
      vline(7, 7, 6, WOOD.dark);
      vline(8, 7, 6, WOOD.mid);
      put(6, 9, METAL.lit); put(9, 9, METAL.lit);
      outline();
      contact(1, 15);
    });
  }

  if (kind === 'toilet') {
    return sprite(({ rect, hline, vline, put, outline, contact }) => {
      rect(4, 1, 8, 5, METAL.body);         // cistern
      hline(4, 1, 8, METAL.lit);
      vline(11, 1, 5, METAL.dark);
      hline(4, 5, 8, METAL.dark);
      put(12, 3, WOOD.lit);                 // brass lever
      rect(3, 6, 10, 6, METAL.body);        // seat, wider than the cistern
      hline(3, 6, 10, METAL.lit);
      rect(5, 7, 6, 4, METAL.mid);
      hline(3, 11, 10, METAL.dark);
      rect(5, 12, 6, 2, METAL.mid);         // pedestal
      hline(5, 13, 6, METAL.dark);
      outline();
      contact(3, 14);
    });
  }

  if (kind === 'bathtub') {
    return sprite(({ rect, hline, vline, put, outline, contact }) => {
      rect(12, 0, 2, 3, METAL.mid);         // spout
      put(12, 0, METAL.lit);
      rect(1, 2, 14, 12, METAL.body);
      hline(1, 2, 14, METAL.lit);
      vline(14, 2, 12, METAL.dark);
      hline(1, 13, 14, METAL.dark);
      rect(3, 4, 10, 8, 20);                // water
      hline(3, 4, 10, 21);
      hline(3, 11, 10, 19);
      put(5, 6, 22); put(9, 9, 22);
      outline();
      contact(1, 15);
    });
  }

  if (kind === 'chest-oak') {
    return sprite(({ rect, hline, vline, put, outline, contact }) => {
      rect(2, 3, 12, 4, WOOD.body);         // lid
      hline(2, 3, 12, WOOD.lit);
      hline(2, 6, 12, WOOD.dark);
      rect(2, 7, 12, 7, WOOD.mid);          // body
      hline(2, 7, 12, WOOD.body);
      vline(13, 7, 7, WOOD.dark);
      hline(2, 13, 12, WOOD.dark);
      for (const x of [4, 11]) {
        vline(x, 3, 11, METAL.mid);
        put(x, 3, METAL.lit);
      }
      rect(7, 6, 2, 3, METAL.lit);          // lock plate
      put(8, 7, INK);
      outline();
      contact(2, 15);
    });
  }

  if (kind === 'chest-iron') {
    return sprite(({ rect, hline, vline, put, outline, contact }) => {
      rect(2, 3, 12, 4, WOOD.mid);
      hline(2, 3, 12, WOOD.body);
      rect(2, 7, 12, 7, WOOD.mid);
      hline(2, 13, 12, WOOD.dark);
      hline(2, 6, 12, METAL.dark);          // iron band along the lid line
      hline(2, 7, 12, METAL.mid);
      for (const x of [3, 12]) {
        vline(x, 3, 11, METAL.mid);
        put(x, 3, METAL.lit);
        put(x, 13, METAL.dark);
      }
      rect(7, 7, 2, 3, METAL.lit);
      put(8, 8, INK);
      outline();
      contact(2, 15);
    });
  }

  if (kind === 'dresser') {
    return sprite(({ rect, hline, vline, outline, contact }) => {
      rect(1, 1, 14, 13, WOOD.mid);
      hline(1, 1, 14, WOOD.lit);            // lit top surface
      hline(1, 2, 14, WOOD.body);
      vline(14, 1, 13, WOOD.dark);
      hline(1, 13, 14, WOOD.dark);
      for (const y of [4, 7, 10]) {
        hline(2, y - 1, 12, WOOD.dark);     // drawer gap
        rect(2, y, 12, 3, WOOD.body);
        rect(7, y + 1, 2, 1, METAL.lit);    // pull
      }
      outline();
      contact(1, 15);
    });
  }

  if (kind === 'nightstand') {
    return sprite(({ rect, hline, vline, outline, contact }) => {
      rect(4, 6, 8, 8, WOOD.mid);
      hline(4, 6, 8, WOOD.lit);
      hline(4, 7, 8, WOOD.body);
      vline(11, 6, 8, WOOD.dark);
      hline(4, 13, 8, WOOD.dark);
      hline(5, 9, 6, WOOD.dark);
      rect(5, 10, 6, 2, WOOD.body);
      rect(7, 11, 2, 1, METAL.lit);
      outline();
      contact(4, 12);
    });
  }

  if (kind === 'standing-lamp') {
    return sprite(({ rect, hline, vline, outline, contact }) => {
      hline(6, 1, 4, PAPER.lit);            // conical shade
      hline(5, 2, 6, PAPER.lit);
      rect(4, 3, 8, 2, PAPER.body);
      hline(3, 5, 10, PAPER.dark);
      vline(7, 6, 6, METAL.mid);            // stem
      vline(8, 6, 6, METAL.body);
      rect(5, 12, 6, 2, METAL.body);        // base
      hline(5, 12, 6, METAL.lit);
      hline(5, 13, 6, METAL.dark);
      outline();
      contact(4, 13);
    });
  }

  if (kind === 'bookshelf') {
    return sprite(({ rect, hline, vline, outline, contact }) => {
      rect(1, 1, 14, 13, WOOD.mid);
      hline(1, 1, 14, WOOD.lit);
      vline(14, 1, 13, WOOD.dark);
      hline(1, 13, 14, WOOD.dark);
      rect(2, 2, 12, 11, WOOD.dark);        // recessed back
      for (const [top, bottom] of [[2, 5], [7, 9], [11, 12]]) {
        for (let x = 3; x < 13; x++) {
          const height = bottom - top + 1 - ((x - 3) % 3 === 1 ? 1 : 0);
          rect(x, bottom - height + 1, 1, height, BOOK_COLORS[(x + top) % BOOK_COLORS.length]);
        }
      }
      hline(2, 6, 12, WOOD.body);           // shelf boards
      hline(2, 10, 12, WOOD.body);
      outline();
      contact(1, 15);
    });
  }

  if (kind === 'stove') {
    return sprite(({ rect, hline, vline, put, outline, contact }) => {
      rect(6, 0, 3, 3, METAL.mid);          // flue
      vline(6, 0, 3, METAL.body);
      rect(1, 2, 14, 12, METAL.body);
      hline(1, 2, 14, METAL.lit);           // cooktop
      hline(1, 4, 14, METAL.mid);
      vline(14, 2, 12, METAL.dark);
      hline(1, 13, 14, METAL.dark);
      for (const x of [4, 10]) { put(x, 3, METAL.dark); put(x + 1, 3, METAL.dark); }
      hline(3, 6, 10, METAL.lit);           // oven handle
      rect(3, 7, 10, 6, METAL.dark);
      rect(4, 8, 8, 3, 28);                 // firebox glow (sole accent)
      hline(4, 10, 8, WOOD.lit);
      outline();
      contact(1, 15);
    });
  }

  if (kind === 'sofa' || kind === 'armchair') {
    const sofa = kind === 'sofa';
    const x0 = sofa ? 1 : 3;
    const width = sofa ? 14 : 10;
    return sprite(({ rect, hline, vline, outline, contact }) => {
      rect(x0, 2, width, 5, CLOTH.body);              // back
      hline(x0, 2, width, CLOTH.lit);
      hline(x0, 6, width, CLOTH.dark);                // shade under the back
      rect(x0, 7, 3, 7, CLOTH.body);                  // arms stand a row proud
      rect(x0 + width - 3, 7, 3, 7, CLOTH.body);
      hline(x0, 7, 3, CLOTH.lit);
      hline(x0 + width - 3, 7, 3, CLOTH.lit);
      vline(x0 + width - 1, 7, 7, CLOTH.dark);
      rect(x0 + 3, 8, width - 6, 6, CLOTH.lit);       // seat cushions
      hline(x0 + 3, 13, width - 6, CLOTH.body);
      if (sofa) vline(x0 + 7, 8, 5, CLOTH.body);      // cushion split
      rect(x0 + 3, 3, 2, 2, 28);                      // one accent cushion
      outline();
      contact(x0, Math.min(15, x0 + width));
    });
  }

  if (kind === 'rug') {
    return sprite(({ rect, hline, vline, put, outline }) => {
      rect(1, 2, 14, 12, CLOTH.body);
      hline(1, 2, 14, CLOTH.dark);
      hline(1, 13, 14, CLOTH.dark);
      vline(1, 2, 12, CLOTH.dark);
      vline(14, 2, 12, CLOTH.dark);
      rect(3, 4, 10, 8, CLOTH.lit);
      rect(6, 6, 4, 4, CLOTH.body);
      rect(7, 7, 2, 2, 28);
      // Fringe tufts break the rectangle so it reads as textile, not a frame.
      for (const x of [2, 4, 6, 8, 10, 12]) { put(x, 1, CLOTH.lit); put(x, 14, CLOTH.lit); }
      outline();
    });
  }

  if (kind === 'desk') {
    return sprite(({ rect, hline, vline, outline, contact }) => {
      rect(3, 1, 4, 2, PAPER.body);         // paper on the desktop
      hline(3, 1, 4, PAPER.lit);
      rect(1, 3, 14, 3, WOOD.body);
      hline(1, 3, 14, WOOD.lit);
      hline(1, 5, 14, WOOD.dark);
      rect(2, 6, 3, 8, WOOD.mid);           // left pedestal
      vline(4, 6, 8, WOOD.dark);
      rect(8, 6, 6, 5, WOOD.mid);           // drawer box
      hline(8, 6, 6, WOOD.body);
      vline(13, 6, 5, WOOD.dark);
      hline(8, 10, 6, WOOD.dark);
      rect(10, 8, 2, 1, METAL.lit);
      rect(11, 11, 3, 3, WOOD.mid);
      vline(13, 11, 3, WOOD.dark);
      outline();
      contact(1, 15);
    });
  }

  if (kind === 'vanity') {
    return sprite(({ rect, hline, vline, put, outline, contact }) => {
      rect(4, 1, 8, 7, WOOD.mid);           // mirror frame
      hline(4, 1, 8, WOOD.lit);
      vline(11, 1, 7, WOOD.dark);
      hline(4, 7, 8, WOOD.dark);
      rect(5, 2, 6, 5, 21);
      hline(5, 6, 6, 20);
      put(6, 3, 22); put(7, 3, 22); put(6, 4, 22);
      rect(1, 8, 14, 2, WOOD.body);         // top
      hline(1, 8, 14, WOOD.lit);
      hline(1, 9, 14, WOOD.dark);
      rect(2, 10, 12, 2, WOOD.mid);         // drawer
      hline(2, 11, 12, WOOD.dark);
      rect(7, 10, 2, 1, METAL.lit);
      for (const x of [2, 12]) { rect(x, 12, 2, 2, WOOD.mid); vline(x + 1, 12, 2, WOOD.dark); }
      outline();
      contact(1, 15);
    });
  }

  if (kind === 'wardrobe') {
    return sprite(({ rect, hline, vline, put, outline, contact }) => {
      rect(1, 1, 14, 2, WOOD.body);         // cornice
      hline(1, 1, 14, WOOD.lit);
      hline(1, 2, 14, WOOD.dark);
      rect(2, 3, 12, 11, WOOD.body);
      vline(13, 3, 11, WOOD.dark);
      hline(2, 13, 12, WOOD.dark);
      rect(3, 4, 4, 8, WOOD.mid);           // door panels
      rect(9, 4, 4, 8, WOOD.mid);
      vline(7, 3, 10, WOOD.dark);
      vline(8, 3, 10, WOOD.mid);
      put(6, 8, METAL.lit); put(9, 8, METAL.lit);
      outline();
      contact(1, 15);
    });
  }

  if (kind === 'potted-plant') {
    return sprite(({ rect, hline, vline, put, outline, contact }) => {
      // Lobed canopy: one lit upper-left mass, body, and a forest underside.
      const canopy = [[6, 9, 1], [5, 10, 2], [4, 11, 3], [3, 12, 4], [3, 12, 5], [4, 11, 6], [6, 9, 7]];
      for (const [left, right, y] of canopy) {
        for (let x = left; x <= right; x++) {
          put(x, y, x <= 7 && y <= 4 ? 14 : (y >= 6 || x >= 11) ? 12 : 13);
        }
      }
      for (const [x, y] of [[4, 5], [11, 4], [8, 2]]) put(x, y, 12);  // leaf notches
      put(7, 8, 12); put(8, 8, 12);
      rect(3, 9, 10, 2, WOOD.body);         // pot rim
      hline(3, 9, 10, WOOD.lit);
      rect(4, 11, 8, 3, WOOD.mid);
      vline(11, 11, 3, WOOD.dark);
      hline(4, 13, 8, WOOD.dark);
      outline();
      contact(3, 14);
    });
  }

  if (kind === 'coat-rack') {
    return sprite(({ rect, hline, vline, put, outline, contact }) => {
      put(7, 0, WOOD.lit); put(8, 0, WOOD.body);      // finial
      rect(7, 1, 2, 12, WOOD.body);
      vline(8, 1, 12, WOOD.dark);
      hline(3, 3, 10, WOOD.mid);                      // peg bar
      put(2, 4, WOOD.dark); put(13, 4, WOOD.dark);
      rect(2, 5, 4, 3, CLOTH.body);                   // hat
      hline(2, 5, 4, CLOTH.lit);
      rect(10, 5, 4, 7, 28);                          // coat (sole accent)
      hline(10, 5, 4, WOOD.lit);
      rect(4, 12, 8, 2, WOOD.mid);                    // foot
      hline(4, 12, 8, WOOD.body);
      hline(4, 13, 8, WOOD.dark);
      outline();
      contact(3, 14);
    });
  }

  if (kind === 'laundry-basket') {
    return sprite(({ rect, hline, vline, outline, contact }) => {
      rect(6, 3, 4, 2, PAPER.body);         // linen spilling over
      hline(6, 3, 4, PAPER.lit);
      rect(4, 5, 8, 2, WOOD.body);          // rim
      hline(4, 5, 8, WOOD.lit);
      rect(4, 7, 8, 4, WOOD.mid);
      rect(5, 11, 6, 3, WOOD.mid);
      vline(11, 7, 4, WOOD.dark);
      vline(10, 11, 3, WOOD.dark);
      hline(5, 13, 6, WOOD.dark);
      hline(5, 9, 3, WOOD.body);            // two weave marks only
      hline(7, 12, 2, WOOD.body);
      outline();
      contact(4, 12);
    });
  }

  if (kind === 'grandfather-clock') {
    return sprite(({ rect, hline, vline, put, outline, contact }) => {
      rect(4, 1, 8, 13, WOOD.body);
      hline(4, 1, 8, WOOD.lit);
      vline(11, 1, 13, WOOD.dark);
      hline(4, 13, 8, WOOD.dark);
      hline(4, 2, 8, WOOD.mid);             // hood band
      rect(5, 3, 6, 4, PAPER.body);         // dial, corners knocked off
      for (const [x, y] of [[5, 3], [10, 3], [5, 6], [10, 6]]) put(x, y, WOOD.mid);
      put(7, 4, INK); put(7, 5, INK); put(8, 5, INK);
      rect(5, 8, 6, 4, WOOD.dark);          // pendulum case
      vline(7, 8, 2, WOOD.lit);
      rect(7, 10, 2, 2, WOOD.lit);
      rect(4, 12, 8, 2, WOOD.mid);          // plinth
      hline(4, 13, 8, WOOD.dark);
      outline();
      contact(4, 12);
    });
  }

  throw new Error(`Unknown interior prop: ${kind}`);
}

const INTERIOR_PROPS = [
  ['cabinet', 'Kitchen cabinet'], ['wall-cabinet', 'Wall cabinet'], ['sink-cabinet', 'Sink cabinet'],
  ['toilet', 'Toilet'], ['bathtub', 'Bathtub'], ['chest-oak', 'Oak chest'], ['chest-iron', 'Ironbound chest'],
  ['dresser', 'Dresser'], ['nightstand', 'Nightstand'], ['standing-lamp', 'Standing lamp'], ['bookshelf', 'Bookshelf'],
  ['stove', 'Kitchen stove'], ['sofa', 'Blue sofa'], ['armchair', 'Blue armchair'], ['rug', 'Patterned rug'],
  ['desk', 'Writing desk'], ['vanity', 'Vanity'], ['wardrobe', 'Wardrobe'], ['potted-plant', 'Potted houseplant'],
  ['coat-rack', 'Coat rack'], ['laundry-basket', 'Laundry basket'], ['grandfather-clock', 'Grandfather clock'],
].map(([key, name]) => plainTile({
  key: `interior-object.${key}`, name, channel: 'onGround', section: 'interiorProps', component: 'onGround',
  pixels: interiorPropPixels(key), collision: key === 'rug' ? {} : { ground: true },
}));

const INTERIOR_PARTITION_AND_FURNITURE = [
  ...[['wood', 'Wood wall'], ['stone', 'Stone wall'], ['cream', 'Cream moulded wall']].map(([kind, name]) => ({
    key: `interior-wall.${kind}`, name, channel: 'wall', mode: 'pipe4',
    connectionGroup: 'interior-wall', paletteSection: 'interiorWalls', palette: ART_PALETTE,
    collision: { wall: true }, occupies: ['wall'],
    components: { wall: interiorWallVariants(kind), roof: interiorWallVariants(kind, 'bottom') },
  })),
  plainTile({ key: 'interior-wall-object.clock', name: 'Wall clock', channel: 'wallObject', section: 'interiorWallObjects', component: 'wall', pixels: clockPixels() }),
  plainTile({ key: 'interior-wall-object.painting', name: 'Landscape painting', channel: 'wallObject', section: 'interiorWallObjects', component: 'wall', pixels: wallDecorationPixels('painting-landscape') }),
  plainTile({ key: 'interior-wall-object.window', name: 'Curtained interior window', channel: 'wallObject', section: 'interiorWallObjects', component: 'wall', pixels: windowPixels(false, true) }),
  plainTile({ key: 'object.bed', name: 'Bed', channel: 'onGround', section: 'groundObjects', component: 'onGround', pixels: furniturePixels('bed'), collision: { ground: true } }),
  plainTile({ key: 'object.table', name: 'Table', channel: 'onGround', section: 'groundObjects', component: 'onGround', pixels: furniturePixels('table'), collision: { ground: true } }),
];

const EXTRA_INTERIOR_WALL_OBJECTS = [
  ['painting-portrait', 'Portrait painting'], ['painting-still-life', 'Still-life painting'],
  ['painting-seascape', 'Seascape painting'], ['mirror', 'Wall mirror'],
  ['shelf', 'Decorated wall shelf'], ['sconce', 'Wall sconce'], ['tapestry', 'Wall tapestry'],
].map(([kind, name]) => plainTile({
  key: `interior-wall-object.${kind}`, name, channel: 'wallObject',
  section: 'interiorWallObjects', component: 'wall', pixels: wallDecorationPixels(kind),
}));

const EXTRA_INTERIOR_WALLS = [
  ['blue', 'Blue plaster wall'], ['green', 'Green plaster wall'], ['red', 'Red plaster wall'],
  ['panel', 'Dark wood panelled wall'], ['wainscot', 'White wainscoted wall'],
].map(([kind, name]) => ({
  key: `interior-wall.${kind}`, name, channel: 'wall', mode: 'pipe4',
  connectionGroup: 'interior-wall', paletteSection: 'interiorWalls', palette: ART_PALETTE,
  collision: { wall: true }, occupies: ['wall'],
  components: { wall: interiorWallVariants(kind), roof: interiorWallVariants(kind, 'bottom') },
}));

export {
  INTERIOR_PARTITION_AND_FURNITURE,
  INTERIOR_PROPS,
  EXTRA_INTERIOR_WALL_OBJECTS,
  EXTRA_INTERIOR_WALLS,
};
